import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePlayer } from '../../lib/PlayerContext';
import { useQuery } from '@tanstack/react-query';
import { fetchAllData } from '../../lib/api';
import { getArtistNames } from '../../utils/artistUtils';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LyricsIcon from '@mui/icons-material/Lyrics';
import { AudioQualityIndicator } from '../AudioQualityIndicator';

interface MiniPlayerProps {
  isVisible: boolean;
  onClose: () => void;
  onMaximize: () => void;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
}

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

const MINI_PLAYER_MIN_WIDTH = 320;
const MINI_PLAYER_MIN_HEIGHT = 160;
const MINI_PLAYER_MAX_WIDTH = 500;
const MINI_PLAYER_MAX_HEIGHT = 240;

export function MiniPlayer({ 
  isVisible, 
  onClose, 
  onMaximize,
  initialPosition = { x: 20, y: 20 },
  initialSize = { width: 370, height: 180 }
}: MiniPlayerProps) {
  const player = usePlayer();
  const { data } = useQuery(['appData'], fetchAllData);
  
  // State for dragging and resizing
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [isVolumeVisible, setIsVolumeVisible] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(player.volume);
  
  // Refs
  const miniPlayerRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible) return;
      
      // Only trigger when mini player is focused or no input is focused
      const isInputFocused = document.activeElement?.tagName === 'INPUT' || 
                            document.activeElement?.tagName === 'TEXTAREA';
      
      if (isInputFocused) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          player.togglePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) {
            player.playNext();
          } else {
            // Seek forward 10 seconds
            const newProgress = Math.min(1, player.progress + (10 / player.duration));
            player.seek(newProgress);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) {
            player.playPrevious();
          } else {
            // Seek backward 10 seconds
            const newProgress = Math.max(0, player.progress - (10 / player.duration));
            player.seek(newProgress);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          player.setVolume(Math.min(1, player.volume + 0.1));
          setIsVolumeVisible(true);
          break;
        case 'ArrowDown':
          e.preventDefault();
          player.setVolume(Math.max(0, player.volume - 0.1));
          setIsVolumeVisible(true);
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          if (player.volume > 0) {
            setPreviousVolume(player.volume);
            player.setVolume(0);
          } else {
            player.setVolume(previousVolume || 0.5);
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          onMaximize();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, player, onMaximize, onClose, previousVolume]);

  // Handle volume visibility timeout
  useEffect(() => {
    if (isVolumeVisible) {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
      volumeTimeoutRef.current = setTimeout(() => {
        setIsVolumeVisible(false);
      }, 2000);
    }
    
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
    };
  }, [isVolumeVisible, player.volume]);

  // Dragging functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target !== dragHandleRef.current && !dragHandleRef.current?.contains(e.target as Node)) {
      return;
    }
    
    e.preventDefault();
    setIsDragging(true);
    
    const rect = miniPlayerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && !isResizing) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;
      
      setPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY))
      });
    }
  }, [isDragging, isResizing, dragOffset, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Resizing functionality
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing && !isDragging) {
      const rect = miniPlayerRef.current?.getBoundingClientRect();
      if (rect) {
        const newWidth = Math.max(
          MINI_PLAYER_MIN_WIDTH,
          Math.min(MINI_PLAYER_MAX_WIDTH, e.clientX - rect.left)
        );
        const newHeight = Math.max(
          MINI_PLAYER_MIN_HEIGHT,
          Math.min(MINI_PLAYER_MAX_HEIGHT, e.clientY - rect.top)
        );
        
        setSize({ width: newWidth, height: newHeight });
      }
    }
  }, [isResizing, isDragging]);

  // Global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleResizeMouseMove, handleMouseUp]);

  // Utility functions
  const getArtistName = (artistId: string) => {
    if (!data) return 'Unknown Artist';
    return getArtistNames(artistId, data.artists);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isVisible || !player.currentSong) return null;

  return (
    <div
      ref={miniPlayerRef}
      className="fixed z-50 bg-surface-dark/95 backdrop-blur-lg border border-surface-border rounded-xl shadow-2xl overflow-hidden transition-all duration-300 select-none elevation-4 hover:elevation-5"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        transform: isVisible ? 'scale(1)' : 'scale(0.9)',
        opacity: isVisible ? 1 : 0,
        boxShadow: 'var(--player-elevation)',
      }}
      role="region"
      aria-label="Mini music player"
      tabIndex={0}
    >
      {/* Drag handle */}
      <div
        ref={dragHandleRef}
        className="flex items-center justify-between p-3 bg-surface-raised/50 cursor-move border-b border-surface-border/50 hover:bg-surface-raised/70 transition-colors duration-200"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center space-x-2">
          <DragIndicatorIcon className="text-text-muted w-4 h-4" />
          <span className="text-text-muted text-xs font-medium">Mini Player</span>
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={onMaximize}
            className="p-1.5 text-text-muted hover:text-pearl-white hover:bg-white/10 rounded transition-all duration-200"
            aria-label="Maximize to full screen player"
            title="Maximize (F)"
          >
            <OpenInFullIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-400/10 rounded transition-all duration-200"
            aria-label="Close mini player"
            title="Close (Esc)"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-3 min-h-0">
        <div className="flex items-start space-x-3 h-full">
          {/* Album art */}
          <div className="flex-shrink-0">
            <img
              src={player.currentSong.coverUrl}
              alt={player.currentSong.title}
              className="w-16 h-16 rounded-lg object-cover shadow-md"
            />
          </div>

          {/* Song info and controls */}
          <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
            {/* Song info */}
            <div className="mb-1">
              <h3 className="text-pearl-white text-sm font-medium truncate">
                {player.currentSong.title}
              </h3>
              <p className="text-text-muted text-xs truncate">
                {getArtistName(player.currentSong.artistId)}
              </p>
            </div>

            {/* Progress bar */}
            <div className="mb-2">
              <div 
                className="relative w-full h-1 bg-surface-border rounded-full cursor-pointer group hover:h-1.5 transition-all duration-200"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const newProgress = Math.max(0, Math.min(1, x / rect.width));
                  player.seek(newProgress);
                }}
              >
                <div
                  className="absolute h-full bg-gradient-to-r from-naia-pink to-soft-violet rounded-full transition-all duration-200 group-hover:from-pink-light group-hover:to-violet-light"
                  style={{ width: `${player.progress * 100}%` }}
                />
                <div
                  className="absolute top-1/2 -mt-1.5 -ml-1.5 w-3 h-3 rounded-full bg-pearl-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md elevation-2"
                  style={{ left: `${player.progress * 100}%` }}
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-text-muted text-xs">
                  {formatTime(player.progress * player.duration)}
                </span>
                <span className="text-text-muted text-xs">
                  {formatTime(player.duration)}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                {/* Shuffle button */}
                <button
                  onClick={player.shuffleQueue}
                  className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 hover:scale-110 rounded-full hover:bg-white/10"
                  aria-label="Toggle shuffle"
                  title="Shuffle"
                >
                  <ShuffleIcon className={`w-3.5 h-3.5 ${player.isShuffled ? 'text-naia-pink' : ''}`} />
                </button>
                
                <button
                  onClick={player.playPrevious}
                  className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 hover:scale-110 rounded-full hover:bg-white/10"
                  aria-label="Previous track"
                  title="Previous (Shift + ←)"
                >
                  <SkipPreviousIcon className="w-4 h-4" />
                </button>
                
                <button
                  onClick={player.togglePlayPause}
                  className="p-1.5 bg-gradient-to-r from-naia-pink to-soft-violet rounded-full text-pearl-white hover:scale-105 transition-all duration-200 shadow-md elevation-2 mx-1"
                  aria-label={player.state === 'playing' ? 'Pause' : 'Play'}
                  title={player.state === 'playing' ? 'Pause (Space)' : 'Play (Space)'}
                >
                  {player.state === 'playing' ? (
                    <PauseIcon className="w-4 h-4" />
                  ) : (
                    <PlayArrowIcon className="w-4 h-4" />
                  )}
                </button>
                
                <button
                  onClick={player.playNext}
                  className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 hover:scale-110 rounded-full hover:bg-white/10"
                  aria-label="Next track"
                  title="Next (Shift + →)"
                >
                  <SkipNextIcon className="w-4 h-4" />
                </button>
                
                {/* Repeat button */}
                <button
                  onClick={player.toggleRepeat}
                  className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 hover:scale-110 rounded-full hover:bg-white/10"
                  aria-label="Toggle repeat"
                  title="Repeat"
                >
                  {player.repeatMode === 'one' ? (
                    <RepeatOneIcon className="w-3.5 h-3.5 text-naia-pink" />
                  ) : (
                    <RepeatIcon className={`w-3.5 h-3.5 ${player.repeatMode !== 'none' ? 'text-naia-pink' : ''}`} />
                  )}
                </button>
              </div>

              {/* Right side controls */}
              <div className="flex items-center space-x-1">
                {/* Audio Quality */}
                <div className="scale-75 origin-center">
                  <AudioQualityIndicator variant="icon" showSettings={false} />
                </div>
                
                {/* Lyrics button */}
                {player.currentSong?.lrcUrl && (
                  <button
                    onClick={() => {
                      // This would trigger a lyrics modal/overlay
                      // For now, we'll just maximize to show lyrics in fullscreen
                      onMaximize();
                    }}
                    className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 hover:scale-110 rounded-full hover:bg-white/10"
                    aria-label="Show lyrics"
                    title="Lyrics"
                  >
                    <LyricsIcon className="w-3.5 h-3.5" />
                  </button>
                )}
                
                {/* Volume control */}
                <div className="relative">
                  <button
                    onClick={() => {
                      if (player.volume > 0) {
                        setPreviousVolume(player.volume);
                        player.setVolume(0);
                      } else {
                        player.setVolume(previousVolume || 0.5);
                      }
                    }}
                    onMouseEnter={() => setIsVolumeVisible(true)}
                    className="p-1 text-text-secondary hover:text-pearl-white transition-all duration-200 rounded-full hover:bg-white/10"
                    aria-label={player.volume > 0 ? 'Mute' : 'Unmute'}
                    title={player.volume > 0 ? 'Mute (M)' : 'Unmute (M)'}
                  >
                    {player.volume > 0 ? (
                      <VolumeUpIcon className="w-4 h-4" />
                    ) : (
                      <VolumeOffIcon className="w-4 h-4" />
                    )}
                  </button>

                  {/* Volume slider */}
                  {isVolumeVisible && (
                    <div 
                      className="absolute bottom-full right-0 mb-2 p-3 bg-surface-dark/95 backdrop-blur-lg border border-surface-border rounded-lg shadow-lg z-10"
                      onMouseEnter={() => setIsVolumeVisible(true)}
                      onMouseLeave={() => setIsVolumeVisible(false)}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-text-muted text-xs w-8">
                          {Math.round(player.volume * 100)}%
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={player.volume}
                          onChange={(e) => player.setVolume(parseFloat(e.target.value))}
                          className="w-20 h-1 bg-surface-border rounded-full outline-none appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, #ff4d8c 0%, #ff4d8c ${player.volume * 100}%, #374151 ${player.volume * 100}%, #374151 100%)`
                          }}
                          aria-label="Volume"
                          title="Volume (↑↓)"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resize handle */}
      <div
        ref={resizeHandleRef}
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize hover:bg-white/10 transition-colors duration-200 rounded-tl-lg"
        onMouseDown={handleResizeMouseDown}
        aria-label="Resize mini player"
      >
        <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-text-muted/50 hover:border-text-muted transition-colors duration-200" />
      </div>

      {/* Keyboard shortcuts info tooltip */}
      <div className="sr-only" aria-live="polite">
        Keyboard shortcuts: Space (play/pause), Arrow keys (seek/volume), M (mute), F (fullscreen), Esc (close)
      </div>
    </div>
  );
}